import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { partyPill } from "./PoliticalIntelligence";
import { OverviewTab, PollsTab, IssuesTab, StrategyTab, BeforeAfterTab } from "./intelligence/tabs";
import { FeedsTab } from "./intelligence/FeedsTab";
import { OppositionTab } from "./intelligence/OppositionTab";
import { DigestSettingsModal } from "./intelligence/DigestSettingsModal";
import { ShareModal } from "./intelligence/ShareModal";
import { Btn } from "../components/UI";
import { toast } from "sonner";
import { Loader2, ArrowLeft, CalendarDays, Mail, Settings2, Share2 } from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "polls", label: "Polls" },
  { key: "issues", label: "Issues" },
  { key: "feeds", label: "Live Feeds" },
  { key: "opposition", label: "Opposition" },
  { key: "strategy", label: "AI Strategy" },
  { key: "beforeafter", label: "Before vs After" },
];

export default function CandidateDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);

  const reload = useCallback(() => {
    api.get(`/political-intelligence/candidates/${id}`).then(r => setData(r.data)).catch(() => navigate("/political-intelligence"));
  }, [id, navigate]);
  useEffect(() => { reload(); }, [reload]);

  const sendDigestTest = async () => {
    setSendingDigest(true);
    try {
      const r = await api.post(`/political-intelligence/candidates/${id}/digest-test`);
      toast.success(`Digest emailed to ${r.data.recipient}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Digest send failed — check the sending domain in War Room"); }
    finally { setSendingDigest(false); }
  };

  if (!data) return <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2 text-cyan-300" />Loading race intelligence…</div>;

  const c = data.candidate;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="candidate-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => navigate("/political-intelligence")} data-testid="back-to-pi"
            className="text-xs text-slate-400 hover:text-cyan-300 inline-flex items-center gap-1 mb-3">
            <ArrowLeft className="w-3.5 h-3.5" /> Political Intelligence
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight" data-testid="candidate-name">{c.candidate_name}</h1>
            <span className={`text-[11px] px-2.5 py-1 rounded-full border ${partyPill(c.party)}`}>{c.party}</span>
          </div>
          <div className="mt-1.5 text-sm text-slate-400 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span>{c.office || "Race"} · {c.state}{c.district && c.district !== "00" ? `-${c.district}` : ""}</span>
            <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-cyan-300/70" /> Election {c.election_date}</span>
            {c.opponents?.length > 0 && <span>vs {c.opponents.map(o => o.name).join(", ")}</span>}
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 uppercase tracking-wider">{c.source === "fec" ? "FEC roster" : "Custom"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 pt-8 flex-wrap">
          <button onClick={() => setShareOpen(true)} data-testid="share-race-btn"
            className={`text-xs px-3 py-2 rounded-full border inline-flex items-center gap-1.5 transition-colors ${c.share_enabled || c.volunteer_enabled ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-300" : "border-white/15 text-slate-400 hover:text-white"}`}>
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
          <button onClick={() => setSettingsOpen(true)} data-testid="digest-settings-btn"
            className={`text-xs px-3 py-2 rounded-full border inline-flex items-center gap-1.5 transition-colors ${c.digest_enabled || c.alert_emails_enabled ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-white/15 text-slate-400 hover:text-white"}`}>
            <Settings2 className="w-3.5 h-3.5" /> Digest & alerts {c.digest_enabled || c.alert_emails_enabled ? "On" : "Off"}
          </button>
          <Btn variant="secondary" onClick={sendDigestTest} disabled={sendingDigest} data-testid="digest-test-btn">
            {sendingDigest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Email me now
          </Btn>
        </div>
      </div>

      <div className="flex gap-1.5 border-b border-white/10 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.key ? "border-cyan-400 text-cyan-300 font-medium" : "border-transparent text-slate-400 hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab data={data} goTo={setTab} />}
      {tab === "polls" && <PollsTab id={id} data={data} reload={reload} />}
      {tab === "issues" && <IssuesTab id={id} data={data} reload={reload} />}
      {tab === "feeds" && <FeedsTab id={id} />}
      {tab === "opposition" && <OppositionTab id={id} data={data} />}
      {tab === "strategy" && <StrategyTab id={id} data={data} reload={reload} />}
      {tab === "beforeafter" && <BeforeAfterTab id={id} data={data} reload={reload} />}

      {settingsOpen && (
        <DigestSettingsModal candidate={c} id={id}
          onClose={() => setSettingsOpen(false)}
          onSaved={() => { setSettingsOpen(false); reload(); }} />
      )}
      {shareOpen && (
        <ShareModal candidate={c} id={id} onClose={() => setShareOpen(false)} reload={reload} />
      )}
    </div>
  );
}
